export const GET_CARRIERS = gql`
  query Carriers($filters: CarrierFiltersInput, $pageInfo: PageInfoInput) {
    carriers(filters: $filters, pageInfo: $pageInfo) {
      items {
        carrierId
        dbaName
        legalName
        dotNumber
        phoneNumber
        faxNumber
        email
        website
        physicalStreet
        physicalCity
        physicalState
        physicalZip
        physicalCountry
        contacts {
          contactId
          contactName
          phoneNumber
          email
          alternativePhones
          dotNumber
          source
          status
          occurrenceCount
        }
      }
      pageInfo {
        offset
        limit
      }
      totalCount
    }
  }
`

export const GET_CARRIER_BY_CARRIER_ID = gql`
  query CarrierByCarrierId($carrierId: String!) {
    carrierByCarrierId(carrierId: $carrierId) {
      carrierId
      dbaName
      legalName
      dotNumber
      phoneNumber
      faxNumber
      email
      website
      physicalStreet
      physicalCity
      physicalState
      physicalZip
      physicalCountry
      contacts {
        contactId
        contactName
        phoneNumber
        email
        alternativePhones
        dotNumber
        source
        status
        occurrenceCount
      }
    }
  }
`

export const GET_CARRIER_BY_DOT_NUMBER = gql`
  query CarrierByDotNumber($dotNumber: String!) {
    carrierByDotNumber(dotNumber: $dotNumber) {
      carrierId
      dbaName
      legalName
      dotNumber
      phoneNumber
      faxNumber
      email
      website
      physicalStreet
      physicalCity
      physicalState
      physicalZip
      physicalCountry
      platformOnly
      directContactNeeded
      verificationPlatforms {
        provider
        notes
        preferred
      }
      contacts {
        contactId
        contactName
        phoneNumber
        email
        alternativePhones
        dotNumber
        source
        status
        occurrenceCount
      }
    }
  }
`

/**
 * Search projection for the admin carrier directory. Deliberately light — no
 * contacts, just enough to tell two similarly named carriers apart in a list.
 */
export const SEARCH_CARRIERS = gql`
  query SearchCarriers(
    $filters: CarrierFiltersInput
    $pageInfo: PageInfoInput
  ) {
    carriers(filters: $filters, pageInfo: $pageInfo) {
      items {
        carrierId
        dbaName
        legalName
        dotNumber
        physicalCity
        physicalState
        email
        contactCount
        curationStatus
        platformOnly
        verificationPlatforms {
          carrierVerificationPlatformId
          provider
          preferred
        }
      }
      totalCount
    }
  }
`

export const GET_ADMIN_CARRIER = gql`
  query AdminCarrier($carrierId: String!) {
    carrierByCarrierId(carrierId: $carrierId) {
      carrierId
      createdAt
      updatedAt
      dbaName
      legalName
      dotNumber
      phoneNumber
      faxNumber
      email
      website
      physicalStreet
      physicalCity
      physicalState
      physicalZip
      physicalCountry
      platformOnly
      verificationPlatforms {
        carrierVerificationPlatformId
        provider
        notes
        preferred
        source
        updatedAt
      }
      curationStatus
      curationChangedAt
      curationNote
      curationChangedBy {
        firstName
        lastName
      }
      contactCount
    }
  }
`

export const GET_CARRIER_CONTACTS = gql`
  query CarrierContacts(
    $carrierId: String!
    $filters: CarrierContactFiltersInput
    $pageInfo: PageInfoInput
  ) {
    carrierContacts(
      carrierId: $carrierId
      filters: $filters
      pageInfo: $pageInfo
    ) {
      items {
        tier
        isBestEmail
        isBestPhone
        contact {
          contactId
          contactName
          email
          phoneNumber
          occurrenceCount
          status
          source
        }
      }
      pageInfo {
        offset
        limit
      }
      totalCount
    }
  }
`

export const UPDATE_CARRIER = gql`
  mutation UpdateCarrier($carrierId: String!, $input: UpdateCarrierInput!) {
    updateCarrier(carrierId: $carrierId, input: $input) {
      carrierId
      dbaName
      legalName
      dotNumber
      phoneNumber
      faxNumber
      email
      website
      physicalStreet
      physicalCity
      physicalState
      physicalZip
      physicalCountry
      platformOnly
      verificationPlatforms {
        carrierVerificationPlatformId
        provider
        notes
        preferred
        source
        updatedAt
      }
      curationStatus
      curationChangedAt
      curationNote
      curationChangedBy {
        firstName
        lastName
      }
      contactCount
      updatedAt
    }
  }
`

export const UPDATE_CARRIER_CURATION_STATUS = gql`
  mutation UpdateCarrierCurationStatus(
    $carrierId: String!
    $status: CarrierCurationStatus!
    $note: String
  ) {
    updateCarrierCurationStatus(
      carrierId: $carrierId
      status: $status
      note: $note
    ) {
      carrierId
      curationStatus
      curationChangedAt
      curationNote
      curationChangedBy {
        firstName
        lastName
      }
      updatedAt
    }
  }
`

export const UPDATE_CARRIER_CONTACT = gql`
  mutation UpdateCarrierContact(
    $contactId: String!
    $input: UpdateCarrierContactInput!
  ) {
    updateCarrierContact(contactId: $contactId, input: $input) {
      tier
      isBestEmail
      isBestPhone
      contact {
        contactId
        contactName
        email
        phoneNumber
        occurrenceCount
        source
      }
    }
  }
`

export const CREATE_CARRIER_CONTACT = gql`
  mutation CreateCarrierContact(
    $carrierId: String!
    $input: CreateCarrierContactInput!
  ) {
    createCarrierContact(carrierId: $carrierId, input: $input) {
      existed
      contact {
        tier
        isBestEmail
        isBestPhone
        contact {
          contactId
          contactName
          email
          phoneNumber
          occurrenceCount
          source
        }
      }
    }
  }
`

export const DELETE_CARRIER_CONTACTS = gql`
  mutation DeleteCarrierContacts($contactIds: [String!]!, $note: String) {
    deleteCarrierContacts(contactIds: $contactIds, note: $note) {
      archived {
        contactId
        contactName
        email
        phoneNumber
        status
      }
      skipped {
        contactId
        contactName
        email
        phoneNumber
        status
      }
    }
  }
`

export const GET_CARRIER_CONTACT_HISTORY = gql`
  query CarrierContactHistory(
    $carrierId: String!
    $filters: CarrierContactHistoryFiltersInput
    $pageInfo: PageInfoInput
  ) {
    carrierContactHistory(
      carrierId: $carrierId
      filters: $filters
      pageInfo: $pageInfo
    ) {
      items {
        auditTrailId
        createdAt
        changeType
        contact {
          contactId
          contactName
          email
          phoneNumber
          occurrenceCount
          carrierId
        }
        actorName
        note
        changes {
          field
          previousValue
          newValue
        }
      }
      pageInfo {
        offset
        limit
      }
      totalCount
    }
  }
`

export const UPDATE_CARRIER_VERIFICATION_ROUTING = gql`
  mutation UpdateCarrierVerificationRouting(
    $carrierId: String!
    $input: UpdateCarrierVerificationRoutingInput!
  ) {
    updateCarrierVerificationRouting(carrierId: $carrierId, input: $input) {
      carrierId
      platformOnly
      verificationPlatforms {
        carrierVerificationPlatformId
        provider
        notes
        preferred
        source
        updatedAt
      }
      updatedAt
    }
  }
`
